import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import posthog from '@/composables/usePostHog.js';
import apiClient from '@/util/apiClient';
import logger from '@/util/logging/logger.js';

export interface MatchDurationGroupReference extends Record<string, unknown> {
	groupNumber: number;
	name?: string | null;
	shortName?: string | null;
}

export interface MatchDurationClientReference extends Record<string, unknown> {
	clientNumber: number;
	name?: string | null;
	shortName?: string | null;
}

export interface MatchDuration extends Record<string, unknown> {
	durationId?: number | null;
	group?: MatchDurationGroupReference | null;
	groupNumber?: MatchDurationGroupReference | number | null;
	client?: MatchDurationClientReference | null;
	clientNumber?: MatchDurationClientReference | number | null;
	sport?: string | null;
	authority?: string | null;
	league?: string | null;
	level?: string | null;
	division?: string | null;
	season?: string | null;
	preMatch?: number | string | null;
	periods?: number | string | null;
	periodMinutes?: number | string | null;
	breaks?: number | string | null;
	breakMinutes?: number | string | null;
	postMatch?: number | string | null;
	extra?: number | string | null;
	travel?: number | string | null;
	createdOn?: string | null;
}

export interface MatchDurationUploadRow extends Record<string, unknown> {
	groupName: string;
	clientNumber: number | null;
	sport: string;
	authority: string;
	league: string | null;
	level: string | null;
	division: string | null;
	season: string | null;
	preMatch: number;
	periods: number;
	periodMinutes: number;
	breaks: number;
	breakMinutes: number;
	postMatch: number;
	extra: number;
	travel: string;
}

export interface MatchDurationUploadPayload {
	durations: MatchDurationUploadRow[];
}

interface DurationsState {
	durationsByGroup: MatchDuration[];
	durationsByGroupNumber: Record<number, MatchDuration[] | undefined>;
}

export const useDurationsStore = defineStore('durationsStore', {
	state: (): DurationsState => ({
		durationsByGroup: [],
		durationsByGroupNumber: {}
	}),
	actions: {
		async fetchDurationsByGroup(groupNumber: number | string): Promise<MatchDuration[]> {
			const normalizedGroupNumber = Number(groupNumber);
			if (!Number.isSafeInteger(normalizedGroupNumber) || normalizedGroupNumber <= 0) return [];
			if (shouldThrottle(`fetchDurationsByGroup:${normalizedGroupNumber}`, 100)) {
				posthog.capture('throttled_action', { action: 'fetchDurationsByGroup', groupNumber: normalizedGroupNumber });
				return this.durationsByGroupNumber[normalizedGroupNumber] ?? [];
			}
			try {
				const response = await apiClient.get<unknown>(`/match/duration/group/${normalizedGroupNumber}`);
				this.durationsByGroup = Array.isArray(response.data) ? (response.data as MatchDuration[]) : [];
				this.durationsByGroupNumber[normalizedGroupNumber] = this.durationsByGroup;
				return this.durationsByGroup;
			} catch (error: unknown) {
				logger.warn(`Failed to fetch durations for group ${normalizedGroupNumber}`, { error });
				this.durationsByGroup = [];
				this.durationsByGroupNumber[normalizedGroupNumber] = [];
				return [];
			}
		},
		async fetchDurationsForGroups(groupNumbers: ReadonlyArray<number | string> | null | undefined): Promise<MatchDuration[][]> {
			const uniqueGroupNumbers = [...new Set((groupNumbers ?? []).map(Number).filter((groupNumber) => Number.isSafeInteger(groupNumber) && groupNumber > 0))];
			return Promise.all(uniqueGroupNumbers.map((groupNumber) => this.fetchDurationsByGroup(groupNumber)));
		},
		async addDuration(duration: MatchDuration): Promise<void> {
			if (shouldThrottle('addDuration', 100)) {
				posthog.capture('throttled_action', { action: 'addDuration' });
				return;
			}
			await apiClient.post('/match/duration/save', duration);
		},
		async uploadDurations(payload: MatchDurationUploadPayload | null | undefined) {
			if (shouldThrottle('uploadDurations', 1000)) {
				posthog.capture('throttled_action', {
					action: 'uploadDurations'
				});

				throw new Error('The match duration upload request was submitted too quickly.');
			}

			if (!payload || !Array.isArray(payload.durations) || payload.durations.length === 0) {
				throw new Error('At least one match duration is required.');
			}

			return apiClient.post('/match/durations/saveAll', payload);
		},
		async updateDuration(duration: MatchDuration): Promise<void> {
			if (shouldThrottle('updateDuration', 100)) {
				posthog.capture('throttled_action', { action: 'updateDuration' });
				return;
			}
			await apiClient.patch(`/match/duration/edit/${duration.durationId}`, duration);
		},
		async deleteDuration(durationId: number | string): Promise<void> {
			if (shouldThrottle('deleteDuration', 100)) {
				posthog.capture('throttled_action', { action: 'deleteDuration' });
				return;
			}
			await apiClient.delete(`/match/duration/delete/${durationId}`);
		}
	}
});
